import { useState } from "react";
import { Alert, Button, FormField, Modal, TextInput, useToast } from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import { useRefundOrderMutation } from "@/modules/orders/services/useRefundOrderMutation";

type RefundSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  orderId: string;
  providerLabel: string;
  maxAmount: number;
  currency: string;
};

// Refund a paid online transaction. Amount defaults to the full paid amount and
// can be lowered for a partial refund (0 < amount ≤ paid).
export function RefundSheet({
  isOpen,
  onClose,
  storeSlug,
  orderId,
  providerLabel,
  maxAmount,
  currency,
}: RefundSheetProps) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const refund = useRefundOrderMutation();
  const [amount, setAmount] = useState(String(maxAmount));

  const parsed = Number(amount);
  const isValid =
    Number.isFinite(parsed) && parsed > 0 && parsed <= maxAmount;
  const maxLabel = formatCurrency(maxAmount, locale, currency);

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await refund.mutateAsync({ storeSlug, orderId, amount: parsed });
      toast.success(t(keys.orders.refundSuccess));
      onClose();
    } catch {
      toast.error(t(keys.orders.refundError));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      elevated
      title={t(keys.orders.refundConfirmTitle)}
      closeLabel={t(keys.common.close)}
      footerActions={
        <Button
          variant="primary"
          size="md"
          disabled={!isValid || refund.isPending}
          onClick={() => void handleSubmit()}
        >
          {refund.isPending
            ? t(keys.common.loading)
            : t(keys.orders.refundAction)}
        </Button>
      }
    >
      <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4">
        <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
          {t(keys.orders.refundConfirmBody, {
            amount: maxLabel,
            provider: providerLabel,
          })}
        </p>
        <FormField label={t(keys.orders.refundAmountLabel)} name="refundAmount">
          <TextInput
            name="refundAmount"
            type="number"
            dir="ltr"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <p className="text-[12px] text-[color:var(--text-muted)]">
          {t(keys.orders.refundAmountHint, { max: maxLabel })}
        </p>
        {!isValid ? (
          <Alert message={t(keys.orders.refundAmountInvalid, { max: maxLabel })} />
        ) : null}
      </div>
    </Modal>
  );
}
